/**
 * ImpersonationBanner — Bandeau persistant affiché en haut de l'app
 * quand le Premier Admin s'est connecté « en tant que » un autre
 * utilisateur (spec iter193, 2026-02-28).
 *
 * Le bouton « Revenir à mon compte » restaure le token d'origine
 * (stocké dans localStorage lors du déclenchement de l'impersonation)
 * puis recharge l'application.
 */

import { useState, useEffect } from "react";
import DeskTip from "@/components/common/DeskTip";
import { Eye, LogOut, Link2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";
import api from "@/lib/api";

const STORAGE_KEY = "ledesk_impersonation_original";

/** Lecture du payload d'origine (si l'admin est en train d'impersonner). */
export function readImpersonationOrigin() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Persiste le token admin d'origine avant de swapper vers un token cible. */
export function beginImpersonation({ adminToken, adminName, targetName, sessionId }) {
  const payload = {
    admin_token: adminToken,
    admin_name: adminName,
    target_name: targetName,
    session_id: sessionId || null,
    started_at: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

/** Restaure le token admin et supprime le flag d'impersonation. */
export function endImpersonation() {
  const orig = readImpersonationOrigin();
  if (!orig) return false;
  // Ferme la session côté backend (fire-and-forget — pas bloquant).
  // Utilise `sendBeacon` si dispo pour survivre à un reload immédiat.
  if (orig.session_id) {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/impersonate/end`;
      const body = JSON.stringify({ session_id: orig.session_id, reason: "manual" });
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true });
      }
    } catch {}
  }
  sessionStorage.setItem("ledesk_token", orig.admin_token);
  localStorage.removeItem(STORAGE_KEY);
  return true;
}

/** iter424 pt 7 — plus de bandeau : l'indice de session est porté par la barre de profil (TopBar). */
export default function ImpersonationBanner() { return null; }

function LegacyImpersonationBanner() { // eslint-disable-line no-unused-vars
  const { user } = useAuth();
  const [origin, setOrigin] = useState(() => readImpersonationOrigin());

  useEffect(() => {
    const onFocus = () => setOrigin(readImpersonationOrigin());
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  if (!origin || !user) return null;

  const handleReturn = () => {
    if (endImpersonation()) {
      toast.success(`Retour au compte ${origin.admin_name}`);
      window.location.href = "/";
    }
  };

  const handleCopyLink = async () => {
    // Spec iter194 (2026-02-28) — copie l'URL exacte de la page
    // actuellement affichée, pour permettre au Premier Admin de la
    // transmettre à l'utilisateur ciblé.
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Lien de cette vue copié dans le presse-papiers");
    } catch {
      toast.error("Copie impossible", { description: "Copiez l'URL manuellement depuis la barre d'adresse" });
    }
  };

  const displayName = origin.target_name
    || `${user.first_name || ""} ${user.last_name || ""}`.trim()
    || user.email;

  return (
    <div
      className="w-full flex items-center justify-between gap-3 px-6 py-2 border-b"
      style={{
        background: "linear-gradient(90deg, #3a2a08 0%, #4a3510 100%)",
        borderColor: "#F5C842",
        color: "#F5C842",
        fontSize: 13,
      }}
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Eye size={16} strokeWidth={2.4} style={{ color: "#F5C842", flexShrink: 0 }} />
        <span className="truncate">
          Vous êtes connecté en tant que{" "}
          <strong style={{ color: "#FFE8A3" }} data-testid="impersonation-target-name">
            {displayName}
          </strong>
          {origin.admin_name && (
            <span className="text-[#F5C842]/70">
              {" "}— session initiée par {origin.admin_name}
            </span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <DeskTip text={"Copier l'URL exacte de cette page pour la transmettre à l'utilisateur"}>
        <button
          type="button"
          onClick={handleCopyLink}
          className="inline-flex items-center gap-1.5 font-semibold transition-all"
          style={{
            height: 28,
            padding: "0 12px",
            borderRadius: 14,
            fontSize: 12,
            background: "transparent",
            color: "#F5C842",
            border: "1px solid #F5C84288",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#F5C84222"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          data-testid="impersonation-copy-link-btn"
          
        >
          <Link2 size={13} strokeWidth={2.4} />
          Copier le lien de cette vue
        </button>
        </DeskTip>
        <DeskTip text={"Revenir à mon compte administrateur"}>
        <button
          type="button"
          onClick={handleReturn}
          className="inline-flex items-center gap-1.5 font-semibold transition-all"
          style={{
            height: 28,
            padding: "0 12px",
            borderRadius: 14,
            fontSize: 12,
            background: "#F5C842",
            color: "#1B1B1B",
            border: "1px solid #F5C842",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(1.1)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.filter = "brightness(1)"; }}
          data-testid="impersonation-return-btn"
          
        >
          <LogOut size={13} strokeWidth={2.4} />
          Revenir à mon compte
        </button>
        </DeskTip>
      </div>
    </div>
  );
}
